import { useState, useCallback } from 'react';
import type { FarmStatus } from '@/lib/farm/status';

export interface Farm {
  id: string;
  name: string;
  status: FarmStatus;
  descriptionShort?: string;
  city?: string;
  county?: string;
  address?: string;
  images?: string[];
  coverImageUrl?: string;
  phone?: string;
  email?: string;
  website?: string;
  instagram?: string;
  categories?: string[];
  description?: string;
  location?: string;
  createdAt: string;
  updatedAt?: string;
}

export const DEMO_FARM: Farm = {
  id: 'demo',
  name: 'Din gård',
  status: 'draft',
  descriptionShort: '',
  city: '',
  county: '',
  images: [],
  createdAt: new Date().toISOString(),
};

export function canSubmitForReview(farm: Farm): boolean {
  return Boolean(
    farm.name &&
    farm.descriptionShort && farm.descriptionShort.length >= 20 &&
    farm.city &&
    farm.county
  );
}

export function getFarmProgress(farm: Farm): { completed: number; total: number } {
  const checks = [
    Boolean(farm.descriptionShort && farm.descriptionShort.length >= 20),
    Boolean(farm.coverImageUrl || (farm.images && farm.images.length > 0)),
    Boolean(farm.city && farm.county),
    farm.status === 'public',
  ];
  return {
    completed: checks.filter(Boolean).length,
    total: checks.length,
  };
}

/**
 * Convert Producer from Supabase to Farm format for UI compatibility.
 * Media fields are no longer sourced from producer entity – use media_assets.
 */
export function producerToFarm(producer: {
  id: string;
  name: string;
  status: FarmStatus;
  short_description?: string | null;
  city?: string | null;
  county?: string | null;
  address_text?: string | null;
  phone?: string | null;
  email?: string | null;
  website?: string | null;
  instagram?: string | null;
  categories?: string[];
  created_at?: string;
  updated_at?: string;
}): Farm {
  return {
    id: producer.id,
    name: producer.name,
    status: producer.status,
    descriptionShort: producer.short_description || '',
    city: producer.city || '',
    county: producer.county || '',
    address: producer.address_text || '',
    coverImageUrl: undefined, // Media from media_assets
    images: [], // Media from media_assets
    phone: producer.phone || undefined,
    email: producer.email || undefined,
    website: producer.website || undefined,
    instagram: producer.instagram || undefined,
    categories: producer.categories || [],
    createdAt: producer.created_at || new Date().toISOString(),
    updatedAt: producer.updated_at,
  };
}

/**
 * Mock hook for managing the current user's farms (LEGACY - use useMyProducers instead)
 */
export function useMyFarms() {
  const [farms, setFarms] = useState<Farm[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const hasFarms = farms.length > 0;

  const createFarm = useCallback(async (name: string): Promise<Farm> => {
    setIsCreating(true);
    await new Promise((resolve) => setTimeout(resolve, 1000));
    const newFarm: Farm = { id: `farm-${Date.now()}`, name, status: 'draft', createdAt: new Date().toISOString() };
    setFarms((prev) => [...prev, newFarm]);
    setIsCreating(false);
    return newFarm;
  }, []);

  const setPublished = useCallback(async (farmId: string, isPublished: boolean): Promise<void> => {
    await new Promise((resolve) => setTimeout(resolve, 800));
    setFarms((prev) => prev.map((farm) => farm.id === farmId ? { ...farm, status: (isPublished ? 'public' : 'draft') as FarmStatus } : farm));
  }, []);

  const updateFarm = useCallback(async (farmId: string, updates: Partial<Farm>): Promise<void> => {
    setIsSaving(true);
    await new Promise((resolve) => setTimeout(resolve, 500));
    setFarms((prev) => prev.map((farm) => farm.id === farmId ? { ...farm, ...updates } : farm));
    setIsSaving(false);
  }, []);

  const getFarm = useCallback((farmId: string): Farm | undefined => {
    return farms.find((farm) => farm.id === farmId);
  }, [farms]);

  return { farms, hasFarms, isLoading, isCreating, isSaving, createFarm, setPublished, updateFarm, getFarm };
}
